import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { ArrowLeft, Save, ChevronRight } from "lucide-react";
import { Toaster, toast } from "sonner";
import PrivacyIcon from "../components/PrivacyIcon";
import { useCoinGate } from "../contexts/CoinGateContext";
import {
  REL_TYPE_OPTIONS_EDIT,
  THEIR_REL_STATUS_OPTIONS,
} from "../constants/relationshipOptions";

// Categorized multi-select options (matching RelationshipEditor style)
const RELATIONSHIP_MATTERS_OPTIONS = [
  { category: "Emotional Foundation", options: [
    { value: "trust_honesty", label: "Trust and honesty" },
    { value: "emotional_safety", label: "Emotional safety" },
    { value: "feeling_understood", label: "Feeling understood and seen" },
    { value: "mutual_respect", label: "Mutual respect" },
  ]},
  { category: "Communication & Connection", options: [
    { value: "open_communication", label: "Open and clear communication" },
    { value: "talk_through_problems", label: "Being able to talk through problems" },
    { value: "feeling_appreciated", label: "Feeling appreciated and valued" },
  ]},
  { category: "Values & Alignment", options: [
    { value: "shared_values", label: "Shared values and principles" },
    { value: "integrity_accountability", label: "Integrity and accountability" },
    { value: "growth_awareness", label: "Growth and self-awareness" },
    { value: "shared_spiritual", label: "Shared spiritual values" },
  ]},
  { category: "Presence & Effort", options: [
    { value: "consistent_effort", label: "Consistent effort and follow-through" },
    { value: "quality_time", label: "Quality time and shared experiences" },
    { value: "reliability_stability", label: "Reliability and stability" },
  ]},
  { category: "Autonomy & Balance", options: [
    { value: "independence_space", label: "Independence and personal space" },
    { value: "support_goals", label: "Support for each other's goals" },
    { value: "healthy_boundaries", label: "Healthy boundaries" },
  ]},
];

const SHUTDOWN_TRIGGERS_OPTIONS = [
  { category: "Communication & Effort", options: [
    { value: "low_effort_replies", label: "Low effort or one-word replies" },
    { value: "talked_over", label: "Being talked over or not listened to" },
    { value: "too_many_questions", label: "Too many questions or long monologues" },
  ]},
  { category: "Emotional Safety", options: [
    { value: "feeling_judged", label: "Feeling judged or criticised" },
    { value: "feelings_dismissed", label: "My feelings being minimised or dismissed" },
  ]},
  { category: "Boundaries & Pace", options: [
    { value: "pressure_to_move", label: "Pressure to move faster than I'm comfortable" },
    { value: "sexual_escalation_early", label: "Sexual escalation too early" },
  ]},
  { category: "Trust & Consistency", options: [
    { value: "dishonesty", label: "Dishonesty or half-truths" },
    { value: "inconsistent_behavior", label: "Inconsistent behaviour or mixed signals" },
  ]},
  { category: "Presence & Energy", options: [
    { value: "overwhelming_intensity", label: "Intensity that feels overwhelming" },
    { value: "emotionally_unavailable", label: "Emotionally unavailable or avoidant behaviour" },
  ]},
  { category: "Digital Behaviour", options: [
    { value: "ghosting", label: "Ghosting or disappearing without explanation" },
    { value: "excessive_messaging", label: "Excessive or intrusive messaging" },
  ]},
  { category: "Values & Respect", options: [
    { value: "disrespectful_controlling", label: "Disrespectful or controlling behaviour" },
    { value: "lack_accountability", label: "Lack of accountability" },
  ]},
];

const ATTRACTION_QUALITIES_OPTIONS = [
  { category: "Energy & Presence", options: [
    { value: "calm_grounded", label: "Calm and grounded" },
    { value: "confident", label: "Confident (not arrogant)" },
    { value: "playful_fun", label: "Playful and fun" },
    { value: "emotionally_present", label: "Emotionally present" },
  ]},
  { category: "Emotional & Relational", options: [
    { value: "kind_considerate", label: "Kind and considerate" },
    { value: "emotionally_open", label: "Emotionally open and honest" },
    { value: "secure_themselves", label: "Secure in themselves" },
  ]},
  { category: "Communication & Mind", options: [
    { value: "clear_direct", label: "Clear and direct communicator" },
    { value: "curious_engaging", label: "Curious and engaging" },
    { value: "good_listener", label: "Good listener" },
  ]},
  { category: "Attraction Style", options: [
    { value: "warm_affectionate", label: "Warm and affectionate" },
    { value: "gently_flirtatious", label: "Gently flirtatious" },
    { value: "direct_interest", label: "Direct about their interest" },
  ]},
];

// Map field keys to categorized options
const CATEGORIZED_OPTIONS_MAP = {
  relationship_matters: RELATIONSHIP_MATTERS_OPTIONS,
  shutdown_triggers: SHUTDOWN_TRIGGERS_OPTIONS,
  attraction_types: ATTRACTION_QUALITIES_OPTIONS,
};

// Multi-select modal component (matching RelationshipEditor style)
const MultiSelectModal = ({ isOpen, onClose, title, options, selected, onSave, maxSelections = 5 }) => {
  // Use key-based reset: when isOpen changes to true, component remounts with new selected value
  const [localSelected, setLocalSelected] = useState(selected || []);
  
  // Reset local state when selected prop changes and modal is open
  const selectedKey = JSON.stringify(selected || []);
  const [prevSelectedKey, setPrevSelectedKey] = useState(selectedKey);
  
  if (isOpen && selectedKey !== prevSelectedKey) {
    setLocalSelected(selected || []);
    setPrevSelectedKey(selectedKey);
  }

  const toggleOption = (value) => {
    if (localSelected.includes(value)) {
      setLocalSelected(localSelected.filter(v => v !== value));
    } else if (localSelected.length < maxSelections) {
      setLocalSelected([...localSelected, value]);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/20 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-divine-gold font-cinzel">{title}</DialogTitle>
          <DialogDescription className="text-white/60">
            Select up to {maxSelections} options ({localSelected.length}/{maxSelections} selected)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {options.map((category) => (
            <div key={category.category}>
              <h4 className="text-white/80 text-sm font-medium mb-2">{category.category}</h4>
              <div className="grid grid-cols-1 gap-2">
                {category.options.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      localSelected.includes(option.value)
                        ? "bg-divine-gold/20 border border-divine-gold/50"
                        : "bg-white/5 border border-transparent hover:bg-white/10"
                    } ${localSelected.length >= maxSelections && !localSelected.includes(option.value) ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={localSelected.includes(option.value)}
                      onChange={() => toggleOption(option.value)}
                      disabled={localSelected.length >= maxSelections && !localSelected.includes(option.value)}
                      className="accent-divine-gold"
                    />
                    <span className="text-white text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/20 text-white/70">
            Cancel
          </Button>
          <Button onClick={() => { onSave(localSelected); onClose(); }} className="bg-divine-gold text-black">
            Save Selection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Field keys that we hide from the edit UI when the match is the user's
// own self-persona (`match_type === "user_persona"`). Existing DB values
// are preserved unchanged — this is purely a UI filter.
const USER_PERSONA_HIDDEN_FIELDS = new Set([
  "persona_connection",
  "relationship_status",
  "history_together",
  "their_feelings_toward_you",
  "your_feelings_toward_them",
  "user_match_goal",
]);

// Section definitions with their fields
const SECTION_CONFIG = {
  basic: {
    title: "Basic Information",
    fields: [
      { key: "name", label: "Name", type: "text" },
      { key: "relationship_status", label: "Define Their Relationship With You", type: "select", options: "relationship_types" },
      { key: "persona_connection", label: "Persona Connection Level", type: "select", options: "connection_levels" },
      { key: "user_match_goal", label: "What is your target goal for this relationship?", type: "textarea" },
    ]
  },
  identity: {
    title: "Personal Identity",
    fields: [
      { key: "sex", label: "Sex", type: "select", options: "sex_options" },
      { key: "pronouns", label: "Pronouns", type: "select", options: "pronoun_options" },
      { key: "sexual_orientation", label: "Sexual Orientation", type: "select", options: "orientation_options" },
      { key: "ethnicity", label: "Ethnicity", type: "text" },
    ]
  },
  appearance: {
    title: "Physical Appearance",
    fields: [
      { key: "vibe_style_presentation", label: "Vibe / Style / Presentation", type: "textarea" },
      { key: "height", label: "Height", type: "text" },
      { key: "weight", label: "Weight", type: "text" },
      { key: "body_type", label: "Body Type", type: "select", options: "body_types" },
      { key: "hair_color", label: "Hair Color", type: "text" },
      { key: "eye_color", label: "Eye Color", type: "text" },
      { key: "complexion", label: "Complexion", type: "text" },
    ]
  },
  demographics: {
    title: "Demographics & Location",
    fields: [
      { key: "living_location", label: "Living Location", type: "text" },
      { key: "birth_location", label: "Birth Location", type: "text" },
      { key: "age", label: "Age", type: "number" },
      { key: "date_of_birth", label: "Date of Birth", type: "date" },
      { key: "time_of_birth", label: "Time of Birth", type: "time" },
    ]
  },
  relationship: {
    title: "Relationship Data",
    fields: [
      { key: "their_relationship_status", label: "Their Relationship Status", type: "select", options: "their_rel_status" },
      { key: "relationship_type", label: "Relationship Type", type: "select", options: "rel_type_options" },
      { key: "their_current_partner", label: "Their Current Partner", type: "text" },
      { key: "their_ex_partner_details", label: "Their Ex Partners", type: "textarea" },
      { key: "libido_intimacy", label: "Libido / Intimacy", type: "textarea" },
      { key: "fetishes", label: "Fetishes", type: "textarea" },
      { key: "history_together", label: "History Together", type: "textarea" },
      { key: "their_feelings_toward_you", label: "Their Feelings Toward You", type: "textarea" },
      { key: "your_feelings_toward_them", label: "Your Feelings Toward Them", type: "textarea" },
      { key: "primary_love_lang", label: "Primary Love Language", type: "select", options: "love_languages" },
      { key: "secondary_love_lang", label: "Secondary Love Language", type: "select", options: "love_languages" },
      { key: "attachment_style", label: "Attachment Style", type: "select", options: "attachment_styles" },
      { key: "relationship_goals", label: "Relationship Goals", type: "textarea" },
      { key: "boundaries", label: "Boundaries", type: "textarea" },
      { key: "seeking_looking_for", label: "Seeking / Looking For", type: "textarea" },
      { key: "turn_offs", label: "Turn Offs", type: "textarea" },
    ]
  },
  personality: {
    title: "Core Personality Signals",
    subsections: [
      {
        name: "Identity & Orientation",
        fields: [
          { key: "self_personality_words", label: "Self-described personality", type: "textarea" },
          { key: "misunderstandings", label: "What people often misunderstand", type: "textarea" },
          { key: "relationship_matters", label: "What matters most in relationships", type: "multiselect", options: "relationship_matters" },
          { key: "top_dealbreakers", label: "Top 3 deal-breakers", type: "textarea" },
        ]
      },
      {
        name: "Emotional Orientation",
        fields: [
          { key: "stress_response", label: "When stressed, I tend to:", type: "select", options: "stress_responses" },
          { key: "connection_style", label: "I feel most connected when:", type: "select", options: "connection_styles" },
        ]
      },
      {
        name: "Communication Preferences",
        fields: [
          { key: "communication_pref_style", label: "Preferred communication style", type: "select", options: "comm_styles" },
          { key: "message_length_pref", label: "Message length preference", type: "select", options: "message_lengths" },
          { key: "conflict_style", label: "Conflict style", type: "select", options: "conflict_styles" },
        ]
      },
      {
        name: "Attachment & Pace",
        fields: [
          { key: "emotional_openness", label: "I open up emotionally:", type: "select", options: "emotional_openness" },
          { key: "needs_consistency", label: "I need consistency to feel safe:", type: "select", options: "yes_no_sometimes" },
          { key: "relationship_feel_pref", label: "I prefer relationships that feel:", type: "select", options: "rel_feels" },
        ]
      },
      {
        name: "Boundaries & Sensitivities",
        fields: [
          { key: "shutdown_triggers", label: "Things that shut me down quickly", type: "multiselect", options: "shutdown_triggers" },
          { key: "topics_avoid_early", label: "Topics I don't like early", type: "textarea" },
          { key: "respect_meaning", label: "What respect looks like to me", type: "textarea" },
        ]
      },
      {
        name: "Energy & Rhythm",
        fields: [
          { key: "social_energy", label: "Social energy", type: "select", options: "social_energy" },
          { key: "connection_rhythm", label: "Ideal connection rhythm", type: "select", options: "connection_rhythms" },
        ]
      },
      {
        name: "Attraction & Desire",
        fields: [
          { key: "attraction_types", label: "I feel attraction when someone is:", type: "multiselect", options: "attraction_qualities" },
          { key: "flirting_style", label: "Flirting style", type: "select", options: "flirting_styles" },
        ]
      },
      {
        name: "Sensory & Lifestyle",
        fields: [
          { key: "activities_light_up", label: "Activities that light me up", type: "textarea" },
          { key: "physical_touch_importance", label: "How important is physical touch? (1-10)", type: "number" },
          { key: "date_vibe_pref", label: "Preferred date vibe", type: "select", options: "date_vibes" },
        ]
      },
    ],
    // Flatten fields for data fetching
    fields: [
      { key: "self_personality_words", label: "Self-described personality", type: "textarea" },
      { key: "misunderstandings", label: "What people often misunderstand", type: "textarea" },
      { key: "relationship_matters", label: "What matters most in relationships", type: "multiselect", options: "relationship_matters" },
      { key: "top_dealbreakers", label: "Top 3 deal-breakers", type: "textarea" },
      { key: "stress_response", label: "When stressed, I tend to:", type: "select", options: "stress_responses" },
      { key: "connection_style", label: "I feel most connected when:", type: "select", options: "connection_styles" },
      { key: "communication_pref_style", label: "Preferred communication style", type: "select", options: "comm_styles" },
      { key: "message_length_pref", label: "Message length preference", type: "select", options: "message_lengths" },
      { key: "conflict_style", label: "Conflict style", type: "select", options: "conflict_styles" },
      { key: "emotional_openness", label: "I open up emotionally:", type: "select", options: "emotional_openness" },
      { key: "needs_consistency", label: "I need consistency to feel safe:", type: "select", options: "yes_no_sometimes" },
      { key: "relationship_feel_pref", label: "I prefer relationships that feel:", type: "select", options: "rel_feels" },
      { key: "shutdown_triggers", label: "Things that shut me down quickly", type: "multiselect", options: "shutdown_triggers" },
      { key: "topics_avoid_early", label: "Topics I don't like early", type: "textarea" },
      { key: "respect_meaning", label: "What respect looks like to me", type: "textarea" },
      { key: "social_energy", label: "Social energy", type: "select", options: "social_energy" },
      { key: "connection_rhythm", label: "Ideal connection rhythm", type: "select", options: "connection_rhythms" },
      { key: "attraction_types", label: "I feel attraction when someone is:", type: "multiselect", options: "attraction_qualities" },
      { key: "flirting_style", label: "Flirting style", type: "select", options: "flirting_styles" },
      { key: "activities_light_up", label: "Activities that light me up", type: "textarea" },
      { key: "physical_touch_importance", label: "How important is physical touch? (1-10)", type: "number" },
      { key: "date_vibe_pref", label: "Preferred date vibe", type: "select", options: "date_vibes" },
    ]
  },
  work: {
    title: "Work & Career",
    fields: [
      { key: "work_details", label: "Current Work", type: "textarea" },
      { key: "work_ambition_goals", label: "Work Ambitions & Goals", type: "textarea" },
      { key: "work_history", label: "Work History", type: "textarea" },
      { key: "education_history", label: "Education History", type: "textarea" },
    ]
  },
  lifestyle: {
    title: "Lifestyle & Interests",
    fields: [
      { key: "hobbies_passions", label: "Hobbies & Passions", type: "textarea" },
      { key: "favourite_music", label: "Favourite Music", type: "textarea" },
      { key: "favourite_movies_series", label: "Favourite Movies/Series", type: "textarea" },
      { key: "favourite_alt_media_blogs", label: "Favourite Alt Media/Blogs", type: "textarea" },
      { key: "favourite_books_audio", label: "Favourite Books/Audio", type: "textarea" },
      { key: "smoking_vape", label: "Smoking / Vape", type: "text" },
      { key: "drinks_alcohol", label: "Drinks Alcohol", type: "text" },
      { key: "car_drive_type", label: "Car / Drive Type", type: "text" },
      { key: "travel_history", label: "Travel History", type: "textarea" },
      { key: "lived_history", label: "Lived History", type: "textarea" },
      { key: "skills_talents", label: "Skills & Talents", type: "textarea" },
      { key: "good_habits_self_care", label: "Good Habits / Self-Care", type: "textarea" },
      { key: "bad_habits", label: "Bad Habits", type: "textarea" },
      { key: "favourite_food_drink", label: "Favourite Food & Drink", type: "textarea" },
      { key: "fears_no_go", label: "Fears / No-Go", type: "textarea" },
    ]
  },
  family: {
    title: "Family",
    fields: [
      { key: "parent_details", label: "Parents/Grandparents", type: "textarea" },
      { key: "sibling_details", label: "Siblings", type: "textarea" },
      { key: "children_details", label: "Children", type: "textarea" },
      { key: "pets_details", label: "Pets", type: "textarea" },
      { key: "family_style_type_details", label: "Family Style / Type", type: "textarea" },
    ]
  },
  attractions: {
    title: "Attractions & Dreams",
    fields: [
      { key: "physical_attraction_type", label: "Physically attracted to", type: "textarea" },
      { key: "fantasies_dreams", label: "Fantasies & Dreams", type: "textarea" },
    ]
  },
  social: {
    title: "Social Dynamics",
    fields: [
      { key: "friends", label: "Friends", type: "textarea" },
      { key: "people_admire", label: "People They Admire", type: "textarea" },
      { key: "people_dislike", label: "People They Dislike", type: "textarea" },
    ]
  },
  values: {
    title: "Values",
    fields: [
      { key: "political_views", label: "Political Views", type: "textarea" },
      { key: "spiritual_views", label: "Spiritual Views", type: "textarea" },
      { key: "religion", label: "Religion", type: "text" },
    ]
  },
  communication: {
    title: "Communication Style",
    fields: [
      { key: "communication_style", label: "Communication Style", type: "textarea" },
      { key: "texting_style", label: "Texting Style", type: "textarea" },
    ]
  },
  bio: {
    title: "Bio & Notes",
    fields: [
      { key: "bio", label: "Bio", type: "textarea" },
      { key: "notes", label: "Notes", type: "textarea" },
      { key: "pasted_data", label: "Raw Pasted Data", type: "textarea" },
    ]
  },
  health: {
    title: "Physical & Mental Health",
    fields: [
      { key: "known_mental_health_diagnosis", label: "Known Mental Health Diagnosis", type: "textarea" },
      { key: "possible_mental_health_issues", label: "Possible Mental Health Issues", type: "textarea" },
      { key: "physical_health_issues", label: "Physical Health Issues", type: "textarea" },
      { key: "medications_being_taken", label: "Medications Being Taken", type: "textarea" },
    ]
  },
};

// Select options definitions
const SELECT_OPTIONS = {
  relationship_types: [
    { value: "stranger", label: "Stranger" },
    { value: "acquaintance", label: "Acquaintance" },
    { value: "friend", label: "Friend" },
    { value: "best_friend", label: "Best Friend" },
    { value: "love_interest", label: "Love Interest" },
    { value: "partner", label: "Partner" },
    { value: "life_partner", label: "Life-Partner" },
    { value: "brother", label: "Brother" },
    { value: "sister", label: "Sister" },
    { value: "step_brother", label: "Step-Brother" },
    { value: "step_sister", label: "Step-Sister" },
    { value: "mother", label: "Mother" },
    { value: "father", label: "Father" },
    { value: "grandmother", label: "Grand-Mother" },
    { value: "grandfather", label: "Grand-Father" },
    { value: "daughter", label: "Daughter" },
    { value: "son", label: "Son" },
    { value: "cousin", label: "Cousin" },
    { value: "work_colleague", label: "Work Colleague" },
    { value: "teacher", label: "Teacher" },
    { value: "boss", label: "Boss" },
    { value: "client", label: "Client" },
    { value: "student", label: "Student" },
  ],
  connection_levels: Array.from({ length: 13 }, (_, i) => ({
    value: String(i + 1),
    label: `L${i + 1}: ${["Spark", "Opening", "Resonance", "Safety", "Curiosity", "Attraction", "Vulnerability", "Alignment", "Momentum", "Intimacy", "Integration", "Commitment", "Union"][i]}`
  })),
  sex_options: [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "trans_male", label: "Trans Male" },
    { value: "trans_female", label: "Trans Female" },
  ],
  pronoun_options: [
    { value: "he_him", label: "He/Him" },
    { value: "she_her", label: "She/Her" },
    { value: "they_them", label: "They/Them" },
    { value: "custom", label: "Other / Custom" },
  ],
  orientation_options: [
    { value: "straight", label: "Straight" },
    { value: "gay", label: "Gay" },
    { value: "lesbian", label: "Lesbian" },
    { value: "bisexual", label: "Bisexual" },
    { value: "pansexual", label: "Pansexual" },
    { value: "asexual", label: "Asexual" },
    { value: "unknown", label: "Unknown / Other" },
  ],
  body_types: [
    { value: "slim", label: "Slim" },
    { value: "athletic", label: "Athletic" },
    { value: "average", label: "Average" },
    { value: "curvy", label: "Curvy" },
    { value: "muscular", label: "Muscular" },
    { value: "heavyset", label: "Heavyset" },
  ],
  their_rel_status: THEIR_REL_STATUS_OPTIONS,
  rel_type_options: REL_TYPE_OPTIONS_EDIT,
  love_languages: [
    { value: "words_of_affirmation", label: "Words of Affirmation" },
    { value: "acts_of_service", label: "Acts of Service" },
    { value: "receiving_gifts", label: "Receiving Gifts" },
    { value: "quality_time", label: "Quality Time" },
    { value: "physical_touch", label: "Physical Touch" },
  ],
  attachment_styles: [
    { value: "secure", label: "Secure" },
    { value: "anxious_preoccupied", label: "Anxious (Preoccupied)" },
    { value: "avoidant_dismissive", label: "Avoidant (Dismissive)" },
    { value: "fearful_avoidant", label: "Fearful-Avoidant" },
    { value: "anxious_avoidant", label: "Anxious-Avoidant" },
    { value: "disorganized", label: "Disorganized" },
    { value: "earned_secure", label: "Earned Secure" },
  ],
  stress_responses: [
    { value: "withdraw", label: "Withdraw" },
    { value: "talk_it_out", label: "Talk it out" },
    { value: "distract_myself", label: "Distract myself" },
    { value: "get_irritable", label: "Get irritable" },
  ],
  connection_styles: [
    { value: "talking_deeply", label: "Talking deeply" },
    { value: "spending_time", label: "Spending time together" },
    { value: "physical_affection", label: "Physical affection" },
    { value: "shared_activities", label: "Shared activities" },
  ],
  comm_styles: [
    { value: "direct", label: "Direct" },
    { value: "gentle", label: "Gentle" },
    { value: "playful", label: "Playful" },
    { value: "reflective", label: "Reflective" },
  ],
  message_lengths: [
    { value: "short", label: "Short" },
    { value: "medium", label: "Medium" },
    { value: "long", label: "Long" },
  ],
  conflict_styles: [
    { value: "avoidant", label: "Avoidant" },
    { value: "collaborative", label: "Collaborative" },
    { value: "assertive", label: "Assertive" },
    { value: "emotional", label: "Emotional" },
  ],
  emotional_openness: [
    { value: "fast", label: "Fast" },
    { value: "slowly", label: "Slowly" },
    { value: "case_by_case", label: "Case-by-case" },
  ],
  yes_no_sometimes: [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "sometimes", label: "Sometimes" },
  ],
  yes_no_maybe: [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "maybe", label: "Maybe" },
  ],
  rel_feels: [
    { value: "calm", label: "Calm" },
    { value: "exciting", label: "Exciting" },
    { value: "deep", label: "Deep" },
    { value: "structured", label: "Structured" },
  ],
  social_energy: [
    { value: "introverted", label: "Introverted" },
    { value: "ambivert", label: "Ambivert" },
    { value: "extroverted", label: "Extroverted" },
  ],
  connection_rhythms: [
    { value: "daily", label: "Daily touchpoints" },
    { value: "few_times_week", label: "A few times a week" },
    { value: "spacious", label: "Spacious" },
  ],
  flirting_styles: [
    { value: "playful", label: "Playful" },
    { value: "direct", label: "Direct" },
    { value: "subtle", label: "Subtle" },
    { value: "expressive", label: "Expressive" },
  ],
  date_vibes: [
    { value: "cozy", label: "Cozy" },
    { value: "adventurous", label: "Adventurous" },
    { value: "intellectual", label: "Intellectual" },
    { value: "sensual", label: "Sensual" },
  ],
  // Multi-select options for personality fields
  relationship_matters: [
    { value: "trust_honesty", label: "Trust and honesty" },
    { value: "open_communication", label: "Open communication" },
    { value: "emotional_support", label: "Emotional support" },
    { value: "respect_boundaries", label: "Respect for boundaries" },
    { value: "shared_values", label: "Shared values and vision" },
    { value: "quality_time", label: "Quality time together" },
    { value: "physical_affection", label: "Physical affection" },
    { value: "independence", label: "Independence and personal space" },
    { value: "humor_playfulness", label: "Humor and playfulness" },
    { value: "growth_together", label: "Growing together" },
    { value: "loyalty_commitment", label: "Loyalty and commitment" },
    { value: "intellectual_connection", label: "Intellectual connection" },
  ],
  shutdown_triggers: [
    { value: "dishonesty", label: "Dishonesty or lying" },
    { value: "dismissiveness", label: "Being dismissed or ignored" },
    { value: "criticism", label: "Harsh criticism or judgment" },
    { value: "control", label: "Controlling behavior" },
    { value: "broken_promises", label: "Broken promises" },
    { value: "lack_effort", label: "Lack of effort or care" },
    { value: "passive_aggression", label: "Passive aggression" },
    { value: "raised_voices", label: "Raised voices or yelling" },
    { value: "stonewalling", label: "Stonewalling or silent treatment" },
    { value: "disrespect", label: "Disrespect or rudeness" },
    { value: "jealousy", label: "Excessive jealousy" },
    { value: "invalidation", label: "Emotional invalidation" },
  ],
  attraction_qualities: [
    { value: "calm_grounded", label: "Calm and grounded" },
    { value: "confident", label: "Confident (not arrogant)" },
    { value: "playful_fun", label: "Playful and fun" },
    { value: "emotionally_present", label: "Emotionally present" },
    { value: "kind_considerate", label: "Kind and considerate" },
    { value: "emotionally_open", label: "Emotionally open and honest" },
    { value: "secure_themselves", label: "Secure in themselves" },
    { value: "clear_direct", label: "Clear and direct communicator" },
    { value: "curious_engaging", label: "Curious and engaging" },
    { value: "good_listener", label: "Good listener" },
    { value: "warm_affectionate", label: "Warm and affectionate" },
    { value: "gently_flirtatious", label: "Gently flirtatious" },
    { value: "direct_interest", label: "Direct about their interest" },
    { value: "intellectual", label: "Intellectually stimulating" },
    { value: "ambitious", label: "Ambitious and driven" },
  ],
};

const SectionEditor = () => {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const [searchParams] = useSearchParams();
  const sectionKey = searchParams.get("section") || "basic";
  
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { requireCoins } = useCoinGate();
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [privacySettings, setPrivacySettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  
  // Multi-select modal state
  const [activeMultiSelectModal, setActiveMultiSelectModal] = useState(null);
  
  const sectionConfig = SECTION_CONFIG[sectionKey];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [matchRes, privacyRes] = await Promise.all([
        authAxios.get(`/matches/${matchId}`),
        authAxios.get(`/privacy-settings/${matchId}`),
      ]);
      
      setMatch(matchRes.data);
      setPrivacySettings(privacyRes.data.field_privacy || {});
      
      // Initialize form data with current values
      const initialData = {};
      SECTION_CONFIG[sectionKey]?.fields.forEach(field => {
        const value = matchRes.data[field.key];
        if (field.type === "multiselect") {
          // Multiselect fields should be arrays
          initialData[field.key] = Array.isArray(value) ? value : [];
        } else {
          initialData[field.key] = value !== null && value !== undefined ? value : "";
        }
      });
      
      setFormData(initialData);
      setOriginalData(initialData);
      setHasChanges(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [matchId, sectionKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFieldChange = (key, value) => {
    setFormData(prev => {
      const newData = { ...prev, [key]: value };
      // Check if data has changed (handle arrays properly)
      const changed = Object.keys(newData).some(k => {
        const newVal = newData[k];
        const origVal = originalData[k];
        // Handle array comparison
        if (Array.isArray(newVal) || Array.isArray(origVal)) {
          const newArr = Array.isArray(newVal) ? newVal : [];
          const origArr = Array.isArray(origVal) ? origVal : [];
          return JSON.stringify(newArr.sort()) !== JSON.stringify(origArr.sort());
        }
        return String(newVal || "") !== String(origVal || "");
      });
      setHasChanges(changed);
      return newData;
    });
  };

  const handlePrivacyChange = (fieldKey, newLevel) => {
    setPrivacySettings(prev => ({
      ...prev,
      [fieldKey]: newLevel
    }));
  };

  const handleSave = async () => {
    if (!(await requireCoins())) return;
    try {
      setSaving(true);
      
      // Clean formData before sending - handle empty values properly
      const cleanedData = {};
      const currentFields = sectionConfig?.fields || [];
      
      for (const field of currentFields) {
        const value = formData[field.key];
        
        // Skip empty values entirely (don't send them)
        if (value === "" || value === null || value === undefined) {
          continue;
        }
        
        // Handle specific field types
        if (field.type === "multiselect") {
          // Multiselect should be array or skip if empty
          if (Array.isArray(value) && value.length > 0) {
            cleanedData[field.key] = value;
          }
        } else if (field.type === "number") {
          // Number fields - convert to int or skip
          const numVal = parseInt(value, 10);
          if (!isNaN(numVal)) {
            cleanedData[field.key] = numVal;
          }
        } else {
          // String fields
          cleanedData[field.key] = value;
        }
      }
      
      console.log("[SectionEditor] Saving cleanedData:", cleanedData);
      const response = await authAxios.patch(`/matches/${matchId}`, cleanedData);
      console.log("[SectionEditor] Save response:", response.data);
      toast.success("Changes saved successfully");
      setOriginalData(formData);
      setHasChanges(false);
      
      // Fire-and-forget: Generate Core Data Set in background
      authAxios.post(`/data-sources/generate-core-dataset/${matchId}`)
        .then(() => toast.success("Core data refreshed", { duration: 2000 }))
        .catch((err) => console.warn("Core data generation failed:", err));
      
      navigate(`/relationships/${matchId}`);
    } catch (error) {
      console.error("[SectionEditor] Error saving:", error);
      console.error("[SectionEditor] Error response:", error.response?.data);
      // Handle Pydantic validation errors (array of objects)
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
        toast.error(`Validation error: ${errorMsg}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error("Failed to save changes");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (hasChanges) {
      setShowUnsavedDialog(true);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/relationships/${matchId}`);
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedDialog(false);
    if (window.history.length > 1) navigate(-1);
    else navigate(`/relationships/${matchId}`);
  };

  const handleSaveAndExit = async () => {
    setShowUnsavedDialog(false);
    await handleSave();
  };

  const renderField = (field) => {
    const value = formData[field.key] || "";
    
    return (
      <div key={field.key} className="space-y-2">
        <label className="text-white/70 text-sm font-medium flex items-center gap-2">
          <PrivacyIcon
            fieldKey={field.key}
            privacyLevel={privacySettings[field.key] || 0}
            matchId={matchId}
            matchName={match?.name}
            onPrivacyChange={handlePrivacyChange}
            size="sm"
          />
          {field.label}
        </label>
        
        {field.type === "text" && (
          <Input
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="bg-white/5 border-white/20 text-white"
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        )}
        
        {field.type === "number" && (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="bg-white/5 border-white/20 text-white"
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        )}
        
        {field.type === "date" && (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="bg-white/5 border-white/20 text-white"
          />
        )}
        
        {field.type === "time" && (
          <Input
            type="time"
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="bg-white/5 border-white/20 text-white"
          />
        )}
        
        {field.type === "textarea" && (
          <Textarea
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="bg-white/5 border-white/20 text-white min-h-[100px]"
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        )}
        
        {field.type === "select" && (
          <>
            <Select
              value={String(value || "")}
              onValueChange={(val) => handleFieldChange(field.key, val)}
            >
              <SelectTrigger className="bg-white/5 border-white/20 text-white">
                <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {SELECT_OPTIONS[field.options]?.map(opt => (
                  <SelectItem 
                    key={opt.value} 
                    value={opt.value}
                    className="text-white hover:bg-white/10"
                  >
                    {opt.label}
                  </SelectItem>
                ))}
                {/* Surface legacy DB values (e.g. "open", "casual",
                    "complicated", "widowed") so the user sees what's actually
                    stored and can re-select a canonical value, rather than
                    the dropdown silently rendering an empty placeholder. */}
                {value &&
                  !SELECT_OPTIONS[field.options]?.find((o) => o.value === value) && (
                    <SelectItem
                      key={`__legacy_${value}`}
                      value={value}
                      className="text-amber-300 hover:bg-white/10"
                    >
                      {value} (legacy)
                    </SelectItem>
                  )}
              </SelectContent>
            </Select>
            {/* Show descriptor if available for selected value */}
            {value && SELECT_OPTIONS[field.options]?.find(opt => opt.value === value)?.descriptor && (
              <p className="text-white/50 text-xs mt-1 italic">
                {SELECT_OPTIONS[field.options].find(opt => opt.value === value).descriptor}
              </p>
            )}
          </>
        )}
        
        {field.type === "multiselect" && (
          <div className="space-y-2">
            {/* Clickable button to open modal */}
            <button
              type="button"
              onClick={() => setActiveMultiSelectModal(field.key)}
              className="w-full flex items-center justify-between p-3 bg-white/5 border border-white/20 rounded-md hover:bg-white/10 hover:border-divine-gold/50 transition-colors text-left"
            >
              <div className="flex-1">
                {(Array.isArray(value) && value.length > 0) ? (
                  <div className="flex flex-wrap gap-1.5">
                    {value.slice(0, 3).map((item) => {
                      // Find label from categorized options
                      const categorizedOpts = CATEGORIZED_OPTIONS_MAP[field.key];
                      let optLabel = item;
                      if (categorizedOpts) {
                        for (const cat of categorizedOpts) {
                          const found = cat.options.find(o => o.value === item);
                          if (found) {
                            optLabel = found.label;
                            break;
                          }
                        }
                      }
                      return (
                        <span 
                          key={item}
                          className="inline-flex items-center px-2 py-0.5 bg-divine-gold/20 text-divine-gold text-xs rounded-full"
                        >
                          {optLabel}
                        </span>
                      );
                    })}
                    {value.length > 3 && (
                      <span className="text-white/50 text-xs self-center">
                        +{value.length - 3} more
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-white/40 text-sm">Tap to select options...</span>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-white/40 ml-2 flex-shrink-0" />
            </button>
            
            {/* Selection count indicator */}
            {Array.isArray(value) && value.length > 0 && (
              <p className="text-xs text-white/50">
                {value.length} of 5 selected
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Get modal config for active multiselect field
  const getActiveModalConfig = () => {
    if (!activeMultiSelectModal) return null;
    
    const field = sectionConfig?.fields.find(f => f.key === activeMultiSelectModal);
    if (!field) return null;
    
    const categorizedOpts = CATEGORIZED_OPTIONS_MAP[activeMultiSelectModal];
    if (!categorizedOpts) return null;
    
    const titleMap = {
      relationship_matters: "What matters most to me in a relationship?",
      shutdown_triggers: "Things that shut me down quickly",
      attraction_types: "I feel attraction when someone is...",
    };
    
    return {
      title: titleMap[activeMultiSelectModal] || field.label,
      options: categorizedOpts,
      selected: formData[activeMultiSelectModal] || [],
    };
  };

  const activeModalConfig = getActiveModalConfig();

  if (loading) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!sectionConfig) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="text-white/60">Section not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster position="top-center" richColors />
      
      {/* Multi-select Modal */}
      {activeModalConfig && (
        <MultiSelectModal
          isOpen={!!activeMultiSelectModal}
          onClose={() => setActiveMultiSelectModal(null)}
          title={activeModalConfig.title}
          options={activeModalConfig.options}
          selected={activeModalConfig.selected}
          onSave={(selected) => handleFieldChange(activeMultiSelectModal, selected)}
          maxSelections={5}
        />
      )}
      
      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Unsaved Changes</DialogTitle>
            <DialogDescription className="text-white/60">
              You have unsaved changes. Would you like to save them before leaving?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleDiscardChanges}
              className="border-white/20 text-white/70"
            >
              Discard
            </Button>
            <Button
              onClick={handleSaveAndExit}
              className="bg-divine-gold text-black hover:bg-divine-gold/90"
            >
              Save & Exit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-white/60 hover:text-white"
                data-testid="section-editor-back"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              
              {/* Mobile: Match Avatar - Links to match profile */}
              {match && (
                <div 
                  className="md:hidden w-10 h-10 rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden border-2 border-divine-gold/30 flex-shrink-0 cursor-pointer hover:border-divine-gold/50 transition-colors"
                  onClick={() => navigate(`/relationships/${matchId}`)}
                  data-testid="header-match-avatar"
                >
                  {(match.thumbnail_url || match.picture_url || match.picture) ? (
                    <img 
                      src={match.thumbnail_url || match.picture_url || match.picture} 
                      alt={match.name} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <span className="text-divine-gold font-cinzel text-sm">{match.name?.[0]}</span>
                  )}
                </div>
              )}
              
              <div>
                <h1 className="font-cinzel text-lg text-white">Edit {sectionConfig.title}</h1>
                <p className="text-white/40 text-sm hidden md:block">{match?.name}</p>
              </div>
            </div>
            
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-divine-gold text-black hover:bg-divine-gold/90 disabled:opacity-50"
              data-testid="section-editor-save-header"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </header>
      
      {/* Form Content */}
      <main className="max-w-2xl mx-auto px-6 py-8">
        <div className="glass-gold rounded-xl p-6 space-y-6">
          {/* Filter out fields that don't make sense for the user's own
              self-persona. Existing DB values are kept; only the form
              widgets are hidden. */}
          {(() => {
            const isUserPersona = match?.match_type === "user_persona";
            const filterFields = (fields) =>
              isUserPersona
                ? fields.filter((f) => !USER_PERSONA_HIDDEN_FIELDS.has(f.key))
                : fields;

            if (sectionConfig.subsections) {
              return sectionConfig.subsections.map((subsection, idx) => {
                const visibleFields = filterFields(subsection.fields);
                if (visibleFields.length === 0) return null;
                return (
                  <div key={subsection.name} className={idx > 0 ? "pt-4 border-t border-white/10" : ""}>
                    <h3 className="text-divine-gold font-cinzel text-lg mb-4">{subsection.name}</h3>
                    <div className="space-y-4">
                      {visibleFields.map(renderField)}
                    </div>
                  </div>
                );
              });
            }
            return filterFields(sectionConfig.fields).map(renderField);
          })()}
        </div>
        
        {/* Bottom Save Button */}
        <div className="mt-8 flex justify-center">
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-divine-gold text-black hover:bg-divine-gold/90 disabled:opacity-50 px-8 py-6 text-lg"
            data-testid="section-editor-save-bottom"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
        
        {hasChanges && (
          <p className="text-center text-amber-400 text-sm mt-4">
            You have unsaved changes
          </p>
        )}
      </main>
    </div>
  );
};

export default SectionEditor;
